import type { ReactNode } from "react";

interface AdminTableToolbarProps {
  children: ReactNode;
  trailing?: ReactNode;
}

interface AdminSearchInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  className?: string;
}

interface AdminSelectOption {
  value: string;
  label: ReactNode;
}

interface AdminSelectFilterProps {
  // label 仅用作无障碍 aria-label，不在界面渲染；筛选条件通过首个选项（如 "All Roles"）体现
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: AdminSelectOption[];
  className?: string;
}

interface AdminSearchButtonProps {
  label: string;
  type?: "submit" | "button";
  onClick?: () => void;
}

interface AdminResetButtonProps {
  label: string;
  onClick: () => void;
}

export function AdminTableToolbar({ children, trailing }: AdminTableToolbarProps) {
  return (
    <div className="mb-4 flex items-center gap-2 flex-wrap">
      {children}
      {trailing && (
        <>
          <div className="flex-1" />
          {trailing}
        </>
      )}
    </div>
  );
}

export function AdminSearchInput({
  value,
  onChange,
  placeholder,
  className = "w-40 lg:w-52 xl:w-72",
}: AdminSearchInputProps) {
  return (
    <div className="relative">
      <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
      <input
        type="search"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg outline-none focus:border-gray-300 bg-white ${className}`}
      />
    </div>
  );
}

export function AdminSelectFilter({
  label,
  value,
  onChange,
  options,
  className = "",
}: AdminSelectFilterProps) {
  return (
    <div className={`relative ${className}`}>
      <select
        aria-label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="appearance-none text-xs border border-gray-200 rounded-lg pl-3 pr-8 py-1.5 outline-none focus:border-gray-300 bg-white cursor-pointer"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <i className="ri-arrow-down-s-line absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none"></i>
    </div>
  );
}

export function AdminSearchButton({ label, type = "submit", onClick }: AdminSearchButtonProps) {
  return (
    <button
      type={type}
      onClick={onClick}
      className="px-3 py-1.5 text-xs font-medium text-white rounded-lg transition-opacity hover:opacity-90 cursor-pointer whitespace-nowrap"
      style={{ backgroundColor: "#830051" }}
    >
      {label}
    </button>
  );
}

export function AdminResetButton({ label, onClick }: AdminResetButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
    >
      <i className="ri-close-line text-sm"></i>
      {label}
    </button>
  );
}

interface AdminSearchFormProps {
  // 草稿输入值，提交后由页面写入正式搜索状态
  value: string;
  onChange: (value: string) => void;
  // 点击 Search 按钮或回车时触发
  onSearch: () => void;
  searchLabel: string;
  placeholder: string;
  onReset?: () => void;
  resetLabel?: string;
  showReset?: boolean;
  inputClassName?: string;
  className?: string;
}

export function AdminSearchForm({
  value,
  onChange,
  onSearch,
  searchLabel,
  placeholder,
  onReset,
  resetLabel = "Reset",
  showReset = false,
  inputClassName,
  className = "flex items-center gap-2",
}: AdminSearchFormProps) {
  return (
    <form
      className={className}
      onSubmit={(e) => {
        e.preventDefault();
        onSearch();
      }}
    >
      <AdminSearchInput
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={inputClassName}
      />
      {showReset && onReset && <AdminResetButton label={resetLabel} onClick={onReset} />}
      <AdminSearchButton label={searchLabel} />
    </form>
  );
}
